import { QueryClient } from "@tanstack/react-query";
import debounce from "lodash/debounce";

import { setPartQueryData } from "#modules/parts/infra/part.query";
import {
  getQuoteRequestLineQueryData,
  setQuoteRequestLineQueryData,
} from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { quoteRequestLinesBulkQuery } from "#modules/quoteRequestLines/infra/quoteRequestLinesBulk.query";
import { QuoteRequestDto } from "#modules/quoteRequests/infra/fetchQuoteRequest.api";
import {
  getQuoteRequestQueryData,
  setQuoteRequestQueryData,
} from "#modules/quoteRequests/infra/quoteRequest.query";

const DEBOUNCE_MS = 500;
const MAX_WAIT_MS = 2000;

type PendingChild = {
  partId: string;
  quoteRequestLineId: string | undefined;
};

type PendingBulk = {
  queryClient: QueryClient;
  childrenByLineId: Map<string, PendingChild>;
};

const pendingByQuoteRequestId = new Map<string, PendingBulk>();

const flushPendingChildren = debounce(
  () => {
    const bulks = [...pendingByQuoteRequestId.entries()];
    pendingByQuoteRequestId.clear();

    bulks.forEach(([quoteRequestId, bulk]) => {
      void flushBulk({ quoteRequestId, bulk });
    });
  },
  DEBOUNCE_MS,
  { maxWait: MAX_WAIT_MS },
);

const flushBulk = async ({
  quoteRequestId,
  bulk,
}: {
  quoteRequestId: string;
  bulk: PendingBulk;
}) => {
  const { queryClient, childrenByLineId } = bulk;
  if (!getQuoteRequestQueryData(queryClient, quoteRequestId)) {
    return;
  }

  const children = [...childrenByLineId.values()];
  const ids = children.map((child) => child.quoteRequestLineId).filter((id): id is string => !!id);

  if (ids.length === 0) {
    return;
  }

  const missingIds = ids.filter((id) => !getQuoteRequestLineQueryData(queryClient, id));

  if (missingIds.length > 0) {
    const lines = await queryClient.fetchQuery(quoteRequestLinesBulkQuery({ ids: missingIds }));
    lines.forEach((line) => {
      setQuoteRequestLineQueryData(queryClient, line.id, line);
      setPartQueryData(queryClient, line.partId, line.part);
    });
  }

  addChildrenToQuoteRequest({
    queryClient,
    quoteRequestId,
    children,
  });
};

const addChildrenToQuoteRequest = ({
  queryClient,
  quoteRequestId,
  children,
}: {
  queryClient: QueryClient;
  quoteRequestId: string;
  children: PendingChild[];
}) => {
  setQuoteRequestQueryData(queryClient, quoteRequestId, (quoteRequest) => {
    if (!quoteRequest) {
      return quoteRequest;
    }

    const existingIds = new Set(quoteRequest.quoteRequestLines.map((line) => line.id));
    const newQuoteRequestLines: QuoteRequestDto["quoteRequestLines"] = children
      .filter(
        (child): child is PendingChild & { quoteRequestLineId: string } =>
          !!child.quoteRequestLineId && !existingIds.has(child.quoteRequestLineId),
      )
      .map((child) => ({
        partId: child.partId,
        id: child.quoteRequestLineId,
        shippingDelay: null,
      }));

    if (newQuoteRequestLines.length === 0) {
      return quoteRequest;
    }

    return {
      ...quoteRequest,
      quoteRequestLines: [...quoteRequest.quoteRequestLines, ...newQuoteRequestLines],
    };
  });
};

export const addChildrenToQuoteRequestDebounced = ({
  queryClient,
  quoteRequestId,
  children,
}: {
  queryClient: QueryClient;
  quoteRequestId: string | undefined;
  children: PendingChild[];
}) => {
  if (!quoteRequestId) {
    return;
  }

  if (!getQuoteRequestQueryData(queryClient, quoteRequestId)) {
    return;
  }

  const existing = pendingByQuoteRequestId.get(quoteRequestId) ?? {
    queryClient,
    childrenByLineId: new Map<string, PendingChild>(),
  };
  existing.queryClient = queryClient;

  children.forEach((child) => {
    if (!child.quoteRequestLineId) {
      return;
    }
    existing.childrenByLineId.set(child.quoteRequestLineId, child);
  });

  if (existing.childrenByLineId.size === 0) {
    return;
  }

  pendingByQuoteRequestId.set(quoteRequestId, existing);
  flushPendingChildren();
};

export const resetAddChildrenToQuoteRequestDebounced = () => {
  flushPendingChildren.cancel();
  pendingByQuoteRequestId.clear();
};
